import { MEMORY_ARCHIVE_LIKE } from "@/lib/leaderMemoryVisibility";
// Guardrail: Veera outputs must remain descriptive and coach-facing.
// Do NOT add scoring, evaluation, or behavior direction here.
// Coach-first rule: Never surface these outputs to leaders without explicit coach action.
import { useCallback, useMemo, useState } from "react";
import { Menu, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLeadershipRecordExperience } from "@/hooks/useContinuityRecordExperience";
import { buildCarryForwardCandidates } from "@/lib/behavior/viewModels/buildCarryForwardCandidates";
import { WorthCarryingForwardPanel } from "@/components/chat/WorthCarryingForwardPanel";
import { ActiveForNextSessionSection } from "@/components/chat/ActiveForNextSessionSection";
import {
  fetchContinuityReviews,
  fetchSuppressedContinuitySourceRowIds,
  formatPostgrestTextInList,
} from "@/lib/behavior/coachContinuityReviewService";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

import { CoachVeeraComposer } from "@/components/chat/CoachVeeraComposer";
import { TrialLockedBotNotice } from "@/components/trial/TrialLockedBotNotice";
import { CoachHarnessPrepBanner } from "@/components/chat/CoachHarnessPrepBanner";
import { CoachLayout } from "@/components/layout/CoachLayout";
import { CoachBriefDrawer } from "@/components/chat/CoachBriefDrawer";
import { CoachContextSidebar } from "@/components/chat/CoachContextSidebar";
import { VeeraThreadContextHeader } from "@/components/chat/VeeraThreadContextHeader";
import { VeeraThreadMessage } from "@/components/chat/VeeraThreadMessage";
import { VeeraThinkingState } from "@/components/chat/VeeraThinkingState";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useCoachVeeraDictation } from "@/hooks/useCoachVeeraDictation";
import { cn } from "@/lib/utils";
import { useCoachAiPageController } from "@/hooks/useCoachAiPageController";

export function CoachAIPage() {
  const {
    activeContextType,
    activeLeader,
    activeThreadId,
    activeThreadSourceLabel,
    briefOpen,
    coachModeFallbackError,
    isCoachScopeTransitionLocked,
    coachIdentityDraftPreview,
    coachRuntime,
    contextTitle,
    filteredLeaders,
    handleDeleteConversation,
    handleLeaderSelection,
    handleNewThread,
    handleOpenCoachIdentityMaterials,
    handleRequestCoachIdentityDraft,
    handleSubmit,
    handleThreadSelect,
    handleUseCoachIdentityDraft,
    handleViewPlans,
    handleWorkTypeSelect,
    coachLensStatus,
    harnessPayload,
    inputRef,
    inputValue,
    isCoachIdentityBuilder,
    isCoachIdentityPhilosophyBuilder,
    isLoading,
    isStreaming,
    premiumActivityChecking,
    premiumActivityExpired,
    premiumActivityLocked,
    latestCoachInsight,
    leaderPickerOpen,
    leaderSearch,
    messages,
    messagesEndRef,
    mobileSidebarOpen,
    newThreadLabel,
    quickPrompts,
    retryCoachModeFallback,
    selectedWorkType,
    sendMessage,
    setBriefOpen,
    setInputValue,
    setLeaderPickerOpen,
    setLeaderSearch,
    setMobileSidebarOpen,
    sidebarThreads,
    streamingContent,
    subtitle,
    coachTranscriptArrivalBody,
    coachTranscriptArrivalTitle,
    urlAction,
  } = useCoachAiPageController();
  const [prepBannerDismissed, setPrepBannerDismissed] = useState(false);
  const [identityBuilderExpanded, setIdentityBuilderExpanded] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const showCoachPracticeContinuity =
    activeContextType === "coach" && !isCoachIdentityBuilder;

  // CGCL-04: fetch leadership record items for the active leader and build carry-forward candidates.
  // Only runs in leader mode; panel renders nothing when candidates is empty.
  const leaderRecordQuery = useLeadershipRecordExperience(
    activeContextType === "leader" ? activeLeader?.id : null,
    user?.id,
  );
  const carryForwardCandidates = useMemo(
    () =>
      buildCarryForwardCandidates({
        items: leaderRecordQuery.isError ? [] : leaderRecordQuery.data?.normalizedItems ?? [],
        leaderId: activeLeader?.id,
      }),
    [leaderRecordQuery.data, leaderRecordQuery.isError, activeLeader?.id],
  );

  // CGCL-06: fetch coach-approved carried-forward reviews for the active leader.
  // Only runs in leader mode. DB filters to carried_forward at the query level;
  // ActiveForNextSessionSection retains a defensive client-side filter as a secondary guard.
  const activeReviewsQuery = useQuery({
    queryKey: ["continuity-reviews-active", activeLeader?.id, user?.id],
    queryFn: async () => {
      if (!user?.id || !activeLeader?.id) return [];
      try {
        const { data, error } = await fetchContinuityReviews({
          supabase,
          coachId: user.id,
          leaderId: activeLeader.id,
          mode: "leader",
          reviewState: "carried_forward",
        });
        if (error) throw error;
        return data ?? [];
      } catch {
        console.error("[CoachAIPage] continuity_reviews_query_failed");
        throw new Error("continuity_reviews_query_failed");
      }
    },
    enabled: !!user?.id && !!activeLeader?.id && activeContextType === "leader",
  });

  // CGCL-07: fetch coach-practice carried-forward reviews for coach mode.
  // No leaderId — these are coach-owned practice threads, not leader-scoped.
  const activePracticeReviewsQuery = useQuery({
    queryKey: ["continuity-reviews-practice-active", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      try {
        const { data, error } = await fetchContinuityReviews({
          supabase,
          coachId: user.id,
          mode: "coach_practice",
          reviewState: "carried_forward",
        });
        if (error) throw error;
        return data ?? [];
      } catch {
        console.error("[CoachAIPage] practice_continuity_reviews_query_failed");
        throw new Error("practice_continuity_reviews_query_failed");
      }
    },
    enabled: !!user?.id && showCoachPracticeContinuity,
  });

  // CGCL-07: fetch recent coach memory nodes for coach-practice carry-forward candidates.
  // These are coach-level nodes (leader_id IS NULL) — not leader-scoped.
  // Mapped to LeadershipRecordItem shape so buildCarryForwardCandidates can process them.
  const practiceCandidatesQuery = useQuery({
    queryKey: ["coach-practice-carry-forward-candidates", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      try {
        const { data: suppressedSourceRowIds, error: suppressionError } =
          await fetchSuppressedContinuitySourceRowIds({
            supabase,
            coachId: user.id,
            mode: "coach_practice",
            sourceTable: "coach_memory_nodes",
          });
        if (suppressionError) throw suppressionError;

        let query = supabase
          .from("coach_memory_nodes")
          .select(
            "id, title, content, node_type, source_id, source_type, confidence, created_at",
          )
          .not("node_type", "like", MEMORY_ARCHIVE_LIKE)
          .eq("coach_id", user.id)
          .is("leader_id", null);
        if (suppressedSourceRowIds && suppressedSourceRowIds.length > 0) {
          query = query.not("id", "in", formatPostgrestTextInList(suppressedSourceRowIds));
        }
        const { data, error } = await query
          .order("created_at", { ascending: false })
          .limit(20);
        if (error) throw error;
        const items = (data ?? []).map((node) => ({
          id: node.id,
          leaderId: undefined,
          sourceType: "coach_note" as const,
          sourceId: node.source_id ?? node.id,
          carryForwardSourceTable: "coach_memory_nodes" as const,
          carryForwardSourceRowId: node.id,
          title: node.title ?? node.node_type ?? "Coaching moment",
          observedFact: node.content,
          createdAt: node.created_at,
          confidence: (node.confidence as "high" | "medium" | "low") ?? "medium",
          visibility: "coach_only" as const,
        }));
        return buildCarryForwardCandidates({
          items,
          coachId: user.id,
          mode: "coach_practice",
        });
      } catch {
        console.error("[CoachAIPage] practice_continuity_query_failed");
        throw new Error("practice_continuity_query_failed");
      }
    },
    enabled: !!user?.id && showCoachPracticeContinuity,
  });

  const activeReviews = activeReviewsQuery.isError ? [] : activeReviewsQuery.data;
  const activePracticeReviews = activePracticeReviewsQuery.isError ? [] : activePracticeReviewsQuery.data;
  const practiceCarryForwardCandidates = practiceCandidatesQuery.isError ? [] : practiceCandidatesQuery.data;

  const handleLeaderCarryForwardAction = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["continuity-reviews-active", activeLeader?.id, user?.id],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "continuity-record-experience",
        "leadership",
        activeLeader?.id,
        user?.id ?? null,
      ],
    });
  }, [queryClient, activeLeader?.id, user?.id]);

  const handlePracticeCarryForwardAction = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["continuity-reviews-practice-active", user?.id],
    });
    queryClient.invalidateQueries({
      queryKey: ["coach-practice-carry-forward-candidates", user?.id],
    });
  }, [queryClient, user?.id]);

  const dictation = useCoachVeeraDictation({
    currentValue: inputValue,
    onValueChange: setInputValue,
  });
  const isEmptyConversation =
    messages.length === 0 && !isStreaming && !isLoading;
  const identityAssistantLabel = isCoachIdentityPhilosophyBuilder
    ? "Coaching philosophy"
    : "Coaching system assistant";
  const identityAssistantSummary = isCoachIdentityPhilosophyBuilder
    ? "Talk through what you believe about how leaders grow. Coach Veera can ask questions, offer examples, compare frameworks, and turn the conversation into a draft for you to review."
    : "Coach Veera is helping you shape My Coaching System. Drafts stay here until you review them in My Coaching System.";
  const identityAssistantPrimaryCopy = isCoachIdentityPhilosophyBuilder
    ? "Think out loud about growth, change, accountability, and trust. Coach Veera can offer examples, compare frameworks, restart, rewrite, or draft language when you are ready."
    : "Use this chat to clarify philosophy, personalize frameworks, refine voice and language, identify guardrails, or organize material.";
  const identityAssistantReviewCopy = isCoachIdentityPhilosophyBuilder
    ? "Coach Veera can turn this conversation into a reviewable draft. You save it from My Coaching System before it shapes Coach Veera."
    : "Nothing changes until you review the draft in My Coaching System.";
  const coachIdentityDraftActionLabel = coachIdentityDraftPreview
    ? isCoachIdentityPhilosophyBuilder
      ? "Review draft before saving"
      : "Review draft in My Coaching System"
    : "Draft for review";
  const emptyStateTitle = isCoachIdentityPhilosophyBuilder
    ? "Start shaping your coaching philosophy"
    : "Start with a focused coach prompt";
  const emptyStateDescription = isCoachIdentityPhilosophyBuilder
    ? "Tell Coach Veera what you believe about growth, change, accountability, and trust. You can think out loud, ask for examples, or draft language."
    : "Pick a starting point, or type your own question below.";

  return (
    <CoachLayout>
      <div className="h-[calc(100vh-3rem)] min-h-0 overflow-hidden px-4 py-4 md:px-6">
        <div className="flex h-full min-h-0 overflow-hidden rounded-xl border border-border bg-card">
          <CoachContextSidebar
            activeWorkType={selectedWorkType}
            activeThreadId={activeThreadId}
            onThreadSelect={handleThreadSelect}
            onNewThread={handleNewThread}
            onWorkTypeSelect={handleWorkTypeSelect}
            newThreadLabel={newThreadLabel}
            threads={sidebarThreads}
            coachLensStatus={coachLensStatus}
            disabled={isCoachScopeTransitionLocked}
            className="hidden md:flex"
          />

          <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <header className="border-b border-border px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center">
                  <button
                    type="button"
                    className="mr-2 rounded p-1 text-muted-foreground hover:bg-muted/60 md:hidden"
                    onClick={() => setMobileSidebarOpen(true)}
                    aria-label="Open navigation"
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold">
                      {contextTitle}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {subtitle}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {latestCoachInsight ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 rounded-full px-3 text-xs"
                      onClick={() => setBriefOpen(true)}
                    >
                      Brief
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteConversation}
                    disabled={!activeThreadId || coachModeFallbackError}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete chat
                  </Button>
                </div>
              </div>
              {activeContextType === "leader" && activeLeader ? (
                <div className="mt-2">
                  <Badge variant="secondary" className="rounded-full px-3">
                    {activeLeader.name}
                  </Badge>
                </div>
              ) : null}
            </header>

            {coachModeFallbackError ? (
              <div className="border-b border-border/70 px-4 py-3">
                <Alert variant="destructive">
                  <AlertTitle>Couldn&apos;t preserve this conversation</AlertTitle>
                  <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      Your existing conversation has not been deleted. Try
                      again before Coach Veera switches views.
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={retryCoachModeFallback}
                    >
                      Try again
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}

            {activeContextType === "coach" &&
            urlAction === "coach-transcript" ? (
              <div className="border-b border-border/70 px-4 py-3">
                <Alert className="border-emerald-500/30 bg-emerald-500/5 dark:border-emerald-400/35 dark:bg-emerald-500/10">
                  <AlertTitle>{coachTranscriptArrivalTitle}</AlertTitle>
                  <AlertDescription>
                    {coachTranscriptArrivalBody}
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}

            {isCoachIdentityBuilder ? (
              <div className="border-b border-border/70 bg-primary/5 px-4 py-2.5 dark:bg-primary/10">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="text-xs uppercase tracking-wide text-primary">
                    {identityAssistantLabel}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {identityAssistantSummary}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant={coachIdentityDraftPreview ? "default" : "outline"}
                    disabled={
                      isLoading ||
                      isStreaming ||
                      premiumActivityChecking ||
                      premiumActivityLocked
                    }
                    onClick={
                      coachIdentityDraftPreview
                        ? handleUseCoachIdentityDraft
                        : handleRequestCoachIdentityDraft
                    }
                    className="h-7 px-2 text-xs"
                  >
                    {coachIdentityDraftActionLabel}
                  </Button>
                  <button
                    type="button"
                    className="text-xs text-primary underline-offset-2 hover:underline"
                    onClick={() => setIdentityBuilderExpanded((v) => !v)}
                    aria-expanded={identityBuilderExpanded}
                  >
                    {identityBuilderExpanded ? "Show less" : "Learn more"}
                  </button>
                </div>
                {identityBuilderExpanded ? (
                  <div className="mt-2 space-y-2 rounded-lg border border-primary/20 bg-background/80 p-3 dark:border-primary/25 dark:bg-background/70">
                    <p className="text-sm text-foreground">
                      {identityAssistantPrimaryCopy}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {identityAssistantReviewCopy}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      This assistant chat drafts from the conversation. Uploads
                      and source materials stay inside My Coaching System.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleOpenCoachIdentityMaterials}
                      >
                        Add materials in My Coaching System
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <ScrollArea className="min-h-0 flex-1 px-4">
              <div
                className={cn(
                  "py-4",
                  isEmptyConversation
                    ? "flex min-h-full flex-col"
                    : "space-y-4",
                )}
              >
                <VeeraThreadContextHeader
                  sourceLabel={activeThreadSourceLabel || ""}
                />

                {isEmptyConversation ? (
                  <div className="flex flex-1 items-center justify-center py-8">
                    <div className="w-full max-w-xl rounded-xl border border-border/70 bg-background/70 px-5 py-5 text-center shadow-sm">
                      <p className="text-sm font-medium text-foreground">
                        {emptyStateTitle}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {emptyStateDescription}
                      </p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        {quickPrompts.slice(0, 3).map((item) => (
                          <Button
                            key={item.id}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 rounded-full px-3 text-xs"
                            onClick={() => void sendMessage(item.prompt)}
                            disabled={
                              isLoading ||
                              coachModeFallbackError ||
                              premiumActivityChecking ||
                              premiumActivityLocked
                            }
                          >
                            {item.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}

                {messages.map((message) => {
                  const role = message.role === "assistant" ? "veera" : "you";
                  return (
                    <div
                      key={message.id}
                      className={cn(
                        "flex",
                        message.role === "user"
                          ? "justify-end"
                          : "justify-start",
                      )}
                    >
                      <div className="max-w-[85%]">
                        <VeeraThreadMessage
                          role={role}
                          text={message.content}
                        />
                      </div>
                    </div>
                  );
                })}

                {isStreaming ? (
                  <div className="flex justify-start">
                    <div className="max-w-[85%]">
                      <VeeraThreadMessage
                        role="veera"
                        text={streamingContent}
                      />
                    </div>
                  </div>
                ) : null}

                {isLoading && !isStreaming ? (
                  <div className="flex justify-start">
                    <div className="max-w-[85%]">
                      <VeeraThinkingState phrase="Veera is thinking" />
                    </div>
                  </div>
                ) : null}

                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* CGCL-07: Coach-practice continuity panels — coach mode only, coach-facing only */}
            {showCoachPracticeContinuity && (activePracticeReviewsQuery.isError || practiceCandidatesQuery.isError) ? (
              <Alert variant="destructive" className="m-4 w-auto">
                <AlertTitle>Unable to load practice continuity</AlertTitle>
                <AlertDescription>Your practice evidence could not be loaded. Please try again.</AlertDescription>
                <Button type="button" variant="outline" className="mt-3" onClick={() => {
                  void activePracticeReviewsQuery.refetch();
                  void practiceCandidatesQuery.refetch();
                }}>
                  Retry continuity
                </Button>
              </Alert>
            ) : showCoachPracticeContinuity ? (
              <>
                <ActiveForNextSessionSection
                  reviews={activePracticeReviews ?? []}
                  mode="coach_practice"
                />
                <WorthCarryingForwardPanel
                  key="coach-practice"
                  candidates={practiceCarryForwardCandidates ?? []}
                  coachId={user?.id}
                  onActionSaved={handlePracticeCarryForwardAction}
                  mode="coach_practice"
                />
              </>
            ) : null}

            {/* CGCL-06: Active for next session — carried-forward reviews, coach-facing only, leader mode only */}
            {activeContextType === "leader" ? (
              <ActiveForNextSessionSection
                mode="leader"
                reviews={activeReviews ?? []}
              />
            ) : null}

            {/* CGCL-04: Worth carrying forward panel — leader mode only, coach-facing only */}
            {/* CGCL-05: coachId enables fire-and-forget persistence of review actions */}
            {activeContextType === "leader" && (leaderRecordQuery.isError || activeReviewsQuery.isError) ? (
              <Alert variant="destructive" className="m-4 w-auto">
                <AlertTitle>Unable to load continuity candidates</AlertTitle>
                <AlertDescription>Recent evidence could not be loaded. Please try again.</AlertDescription>
                <Button type="button" variant="outline" className="mt-3" onClick={() => {
                  void leaderRecordQuery.refetch();
                  void activeReviewsQuery.refetch();
                }}>
                  Retry continuity
                </Button>
              </Alert>
            ) : activeContextType === "leader" && leaderRecordQuery.isLoading ? (
              <p role="status" className="px-4 py-3 text-sm text-muted-foreground">Loading continuity candidates</p>
            ) : activeContextType === "leader" ? (
              <WorthCarryingForwardPanel
                key={activeLeader?.id ?? "no-leader"}
                mode="leader"
                candidates={carryForwardCandidates}
                coachId={user?.id}
                onActionSaved={handleLeaderCarryForwardAction}
              />
            ) : null}

            <div className="border-t border-border/70 p-4 space-y-3">
              {premiumActivityLocked ? (
                <TrialLockedBotNotice
                  trialExpired={premiumActivityExpired}
                  onViewPlans={handleViewPlans}
                />
              ) : null}
              {!prepBannerDismissed &&
                harnessPayload &&
                harnessPayload.prepSuggestions.length > 0 && (
                  <CoachHarnessPrepBanner
                    suggestions={harnessPayload.prepSuggestions}
                    onDismiss={() => setPrepBannerDismissed(true)}
                  />
                )}
              <CoachVeeraComposer
                value={inputValue}
                onValueChange={setInputValue}
                onSubmit={handleSubmit}
                placeholder={
                  premiumActivityLocked
                    ? "Available with Intelligence"
                    : premiumActivityChecking
                      ? "Checking access..."
                      : "Message Coach Veera..."
                }
                disabled={
                  isLoading ||
                  coachModeFallbackError ||
                  premiumActivityChecking ||
                  premiumActivityLocked
                }
                submitDisabled={
                  !inputValue.trim() ||
                  isLoading ||
                  coachModeFallbackError ||
                  premiumActivityChecking ||
                  premiumActivityLocked
                }
                inputRef={inputRef}
                controlsClassName="items-center"
                inputClassName="h-11 rounded-xl focus-visible:ring-1 focus-visible:ring-offset-0"
                micButtonClassName="h-11 w-11 p-0"
                sendButtonClassName="h-11 w-11 bg-violet-600 p-0 hover:bg-violet-700"
                dictation={dictation}
              />
            </div>
          </section>
        </div>

        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-[17.5rem] p-0">
            <CoachContextSidebar
              activeWorkType={selectedWorkType}
              activeThreadId={activeThreadId}
              onThreadSelect={(id) => {
                handleThreadSelect(id);
                setMobileSidebarOpen(false);
              }}
              onNewThread={() => {
                handleNewThread();
                setMobileSidebarOpen(false);
              }}
              onWorkTypeSelect={(wt) => {
                handleWorkTypeSelect(wt);
                setMobileSidebarOpen(false);
              }}
              newThreadLabel={newThreadLabel}
              threads={sidebarThreads}
              coachLensStatus={coachLensStatus}
              disabled={isCoachScopeTransitionLocked}
            />
          </SheetContent>
        </Sheet>

        <Dialog open={leaderPickerOpen} onOpenChange={setLeaderPickerOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Choose a leader</DialogTitle>
              <DialogDescription>
                Start a new leader-analysis thread by picking the leader you
                want Coach Veera to focus on.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                value={leaderSearch}
                onChange={(event) => setLeaderSearch(event.target.value)}
                placeholder="Search leaders"
                autoFocus
              />
              <div className="space-y-2">
                {filteredLeaders.length > 0 ? (
                  filteredLeaders.map((leader) => (
                    <button
                      key={leader.id}
                      type="button"
                      onClick={() => handleLeaderSelection(leader.id)}
                      className="flex w-full items-center justify-between rounded-xl border border-border px-3 py-3 text-left transition-colors hover:bg-muted/40"
                    >
                      <div>
                        <p className="text-sm font-medium">{leader.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {leader.behaviorName || "Leader analysis"}
                        </p>
                      </div>
                      <Badge variant="secondary" className="rounded-full">
                        Analyze
                      </Badge>
                    </button>
                  ))
                ) : (
                  <p className="rounded-xl border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
                    No leaders matched that search.
                  </p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <CoachBriefDrawer
          open={briefOpen}
          onClose={() => setBriefOpen(false)}
          coachInsight={latestCoachInsight}
          harnessPayload={harnessPayload}
          carriedForwardReviews={
            activeContextType === "leader"
              ? (activeReviews ?? [])
              : showCoachPracticeContinuity
                ? (activePracticeReviews ?? [])
                : []
          }
        />
      </div>
    </CoachLayout>
  );
}
